import React, { useEffect, useMemo, useState } from 'react'
import { Download, Filter, RefreshCw, Calendar, Package, User as UserIcon, ListFilter } from 'lucide-react'
import stockService from '../services/stockService'
import userService, { User } from '../services/userService'

type MovementRow = {
  produtoCodigo?: string
  produtoNome?: string
  quantidadeDisponivel?: number
  tipo?: string
  quantidade?: number
  usuario?: string
  origemEmpresaNome?: string
  destinoEmpresaNome?: string
  observacoes?: string
  dataMovimento?: string
}

type AggregatedRow = {
  produtoCodigo?: string
  produtoNome?: string
  quantidadeDisponivel?: number
  tipo?: string
  quantidadeSum: number
  vendasCount: number
  usuario?: string
  origemEmpresaNome?: string
  destinoEmpresaNome?: string
  observacoesList: string[]
  lastDate?: string
}

function toISODate(date: Date): string {
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

function normalizeReportPayload(payload: any): any[] {
  const raw = payload?.data ?? payload
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw?.content)) return raw.content
  if (raw && typeof raw === 'object') return [raw]
  return []
}

function mapToMovementRows(obj: any): MovementRow[] {
  const products = Array.isArray(obj) ? obj : [obj]
  const rows: MovementRow[] = []
  for (const p of products) {
    const produtoCodigo = p?.produtoCodigo
    const produtoNome = p?.produtoNome
    const quantidadeDisponivel = Number(p?.quantidadeDisponivel ?? 0)
    const grupos = Array.isArray(p?.grupos) ? p.grupos : []
    for (const g of grupos) {
      const tipo = g?.tipoMovimentacao
      const movimentacoes = Array.isArray(g?.movimentacoes) ? g.movimentacoes : []
      for (const m of movimentacoes) {
        const quantidade = Number(m?.quantidade ?? 0)
        const usuario = m?.usuarioNome ?? m?.usuario
        const origemEmpresaNome = m?.origemEmpresaNome
        const destinoEmpresaNome = m?.destinoEmpresaNome
        const observacoes = m?.observacao ?? m?.observacoes
        const dataMovimento = m?.dataMovimentacao ?? m?.dataMovimento
        rows.push({
          produtoCodigo,
          produtoNome,
          quantidadeDisponivel: isNaN(quantidadeDisponivel) ? undefined : quantidadeDisponivel,
          tipo,
          quantidade: isNaN(quantidade) ? undefined : quantidade,
          usuario,
          origemEmpresaNome,
          destinoEmpresaNome,
          observacoes,
          dataMovimento,
        })
      }
    }
  }
  return rows
}

const StockMovementReportPage: React.FC = () => {
  const [dataInicial, setDataInicial] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return toISODate(d)
  })
  const [dataFinal, setDataFinal] = useState<string>(() => toISODate(new Date()))
  const [codigoProduto, setCodigoProduto] = useState<string>('')
  const [vendedorId, setVendedorId] = useState<string>('')
  const [tipoMovimentacao, setTipoMovimentacao] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [entries, setEntries] = useState<MovementRow[]>([])
  const [movementTypes, setMovementTypes] = useState<string[]>([])
  const [sellers, setSellers] = useState<User[]>([])

  useEffect(() => {
    ;(async () => {
      try {
        const tp = await stockService.getMovementTypes()
        setMovementTypes(tp.data)
      } catch {}
      try {
        const res = await userService.getUsersByRole('VENDEDOR')
        let arr = Array.isArray(res?.data) ? (res.data as User[]) : []
        if (!arr.length) {
          const paged = await userService.getUsers({ role: 'VENDEDOR', size: 500, ativo: true })
          arr = Array.isArray(paged?.data?.content) ? (paged.data.content as User[]) : []
        }
        setSellers(arr)
      } catch {
        try {
          const paged = await userService.getUsers({ role: 'VENDEDOR', size: 500, ativo: true })
          const arr = Array.isArray(paged?.data?.content) ? (paged.data.content as User[]) : []
          setSellers(arr)
        } catch {
          setSellers([])
        }
      }
    })()
  }, [])


  const buscar = async () => {
    setLoading(true)
    try {
      const response = await stockService.relatorioMovimentos({
        dataInicio: dataInicial,
        dataFim: dataFinal,
        codigoProduto: codigoProduto || undefined,
        vendedorId: vendedorId || undefined,
        tipo: tipoMovimentacao || undefined,
      })
      const list = normalizeReportPayload(response.data)
      const rows = list.flatMap(mapToMovementRows)
      rows.sort((a, b) => {
        const pa = String(a.produtoCodigo || '')
        const pb = String(b.produtoCodigo || '')
        if (pa !== pb) return pa.localeCompare(pb)
        const ta = String(a.tipo || '')
        const tb = String(b.tipo || '')
        if (ta !== tb) return ta.localeCompare(tb)
        const da = new Date(a.dataMovimento || 0).getTime()
        const db = new Date(b.dataMovimento || 0).getTime()
        return da - db
      })
      setEntries(rows)
    } catch (err) {
      setEntries([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    buscar()
  }, [])

  const grouped = useMemo(() => {
    const map = new Map<string, { produtoNome?: string; quantidadeDisponivel?: number; tipos: Map<string, AggregatedRow[]> }>()
    for (const r of entries) {
      const pkey = String(r.produtoCodigo || '-')
      const tkey = String(r.tipo || '-')
      if (!map.has(pkey)) map.set(pkey, { produtoNome: r.produtoNome, quantidadeDisponivel: r.quantidadeDisponivel, tipos: new Map() })
      const prod = map.get(pkey)!
      if (!prod.tipos.has(tkey)) prod.tipos.set(tkey, [])
      const arr = prod.tipos.get(tkey)!

      const obsText = String(r.observacoes || '').trim()
      const obsLabel = obsText ? obsText.replace(/\s+/g, ' ') : ''
      const isSale = /venda/i.test(obsLabel)

      let idx = -1
      for (let i = 0; i < arr.length; i++) {
        const a = arr[i]
        if (
          String(a.usuario || '-') === String(r.usuario || '-') &&
          String(a.origemEmpresaNome || '-') === String(r.origemEmpresaNome || '-') &&
          String(a.destinoEmpresaNome || '-') === String(r.destinoEmpresaNome || '-')
        ) {
          idx = i
          break
        }
      }
      if (idx >= 0) {
        const a = arr[idx]
        a.quantidadeSum = (a.quantidadeSum || 0) + (r.quantidade || 0)
        a.vendasCount = (a.vendasCount || 0) + (isSale ? 1 : 0)
        const setObs = new Set(a.observacoesList)
        if (obsLabel) setObs.add(obsLabel)
        a.observacoesList = Array.from(setObs)
        const ts = r.dataMovimento ? new Date(r.dataMovimento).getTime() : 0
        const prev = a.lastDate ? new Date(a.lastDate).getTime() : 0
        if (ts > prev) a.lastDate = r.dataMovimento
      } else {
        arr.push({
          produtoCodigo: r.produtoCodigo,
          produtoNome: r.produtoNome,
          quantidadeDisponivel: r.quantidadeDisponivel,
          tipo: r.tipo,
          quantidadeSum: r.quantidade || 0,
          vendasCount: isSale ? 1 : 0,
          usuario: r.usuario,
          origemEmpresaNome: r.origemEmpresaNome,
          destinoEmpresaNome: r.destinoEmpresaNome,
          observacoesList: obsLabel ? [obsLabel] : [],
          lastDate: r.dataMovimento,
        })
      }
    }
    return map
  }, [entries])

  const exportarCSV = () => {
    const header = [
      'Código Produto',
      'Nome Produto',
      'Qtde Disponível',
      'Tipo Movimentação',
      'Qtde Movimento',
      'Usuário',
      'Origem Empresa',
      'Destino Empresa',
      'Qtde Vendas',
      'Data Movimentação',
    ]
    const aggRows: AggregatedRow[] = []
    Array.from(grouped.entries()).forEach(([codigo, info]) => {
      Array.from(info.tipos.entries()).forEach(([tipo, list]) => {
        list.forEach((a) => {
          aggRows.push({ ...a, produtoCodigo: codigo, produtoNome: info.produtoNome, quantidadeDisponivel: info.quantidadeDisponivel, tipo })
        })
      })
    })
    const rows = aggRows.map((e) => [
      e.produtoCodigo ?? '',
      e.produtoNome ?? '',
      typeof e.quantidadeDisponivel === 'number' ? String(e.quantidadeDisponivel).replace('.', ',') : '',
      e.tipo ?? '',
      typeof e.quantidadeSum === 'number' ? String(e.quantidadeSum).replace('.', ',') : '',
      e.usuario ?? '',
      e.origemEmpresaNome ?? '',
      e.destinoEmpresaNome ?? '',
      typeof e.vendasCount === 'number' ? String(e.vendasCount) : '',
      e.lastDate ?? '',
    ])
    const csvLines = [header, ...rows]
      .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n')
    const blob = new Blob(["\ufeff" + csvLines], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `relatorio-estoque-movimentos-${dataInicial}_a_${dataFinal}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">Relatório de estoque e movimentação</h1>
          <p className="text-gray-600 mt-1">Filtre por período, produto, vendedor e tipo; exporte para Excel</p>
        </div>
        <div className="flex gap-3">
          <button onClick={buscar} className="berry-button-secondary flex items-center gap-2" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
          <button onClick={exportarCSV} className="berry-button-primary flex items-center gap-2" disabled={entries.length === 0}>
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="berry-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Código do produto</label>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              <input type="text" className="berry-input w-full" value={codigoProduto} onChange={(e) => setCodigoProduto(e.target.value)} placeholder="Ex.: P9" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vendedor</label>
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-gray-500" />
              <select className="berry-input w-full" value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}>
                <option value="">Todos</option>
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome || s.username}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de movimentação</label>
            <div className="flex items-center gap-2">
              <ListFilter className="w-4 h-4 text-gray-500" />
              <select className="berry-input w-full" value={tipoMovimentacao} onChange={(e) => setTipoMovimentacao(e.target.value)}>
                <option value="">Todos</option>
                {movementTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={buscar} className="berry-button flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Aplicar filtros
          </button>
        </div>
      </div>


      <div className="berry-card p-0 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código Produto</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome Produto</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qtde Disponível</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qtde Movimento</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origem</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destino</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qtde Vendas</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entries.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={10}>Nenhum resultado para os filtros informados</td>
              </tr>
            )}
            {Array.from(grouped.entries()).map(([codigo, info]) => (
              <React.Fragment key={codigo}>
                {Array.from(info.tipos.entries()).map(([tipo, list], i) => (
                  <React.Fragment key={`${codigo}-${tipo}-${i}`}>
                    {list.map((a, idx) => (
                      <tr key={`${codigo}-${tipo}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-700">{codigo || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{info.produtoNome ?? '-'}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{typeof info.quantidadeDisponivel === 'number' ? info.quantidadeDisponivel : '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{tipo || '-'}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{typeof a.quantidadeSum === 'number' ? a.quantidadeSum : '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{a.usuario ?? '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{a.origemEmpresaNome ?? '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{a.destinoEmpresaNome ?? '-'}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-700">{typeof a.vendasCount === 'number' ? a.vendasCount : '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-700">{a.lastDate ? new Date(a.lastDate).toLocaleString('pt-BR') : '-'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default StockMovementReportPage